export interface PromptIdea {
  question: string;
  reason: string;
}

export interface ElsieSummaryData {
  kwelloIds: string[];
  referenceData: Citation[];
}
export interface ElsieAiModel {
  conversationId: string;
  targetCitation?: string;
  targetCitation2?: Citation;
  citations: string[];
  citations2: Citation[];
  isViewAllCitations: boolean;
  citationsOrder: Map<string, number>;
  promptIdeas?: PromptIdea[];
  isPromptIdeasOpen: boolean;
  isCitationsOpen: boolean;
  totalTokens: number;
  disableAIOperations: boolean;
  elsieSummaryData?: ElsieSummaryData;
  uploadedFiles?: File[];
  prompt?: string;
  dataSources?: string[];
  chatHistory: ChatHistoryState;
}
export interface SetConversationIdPayload {
  conversationId: string;
}

export interface SetTargetCitationPayload {
  targetCitation: string | undefined;
}
export interface SetTargetCitation2Payload {
  targetCitation2: Citation | undefined;
}

export interface SetCitationsPayload {
  citations: string[];
}
export interface SetCitations2Payload {
  citations: Citation[];
}
export interface SetCitationsOrderPayload {
  citationsOrder: Map<string, number>;
}

export interface SetPromptIdeasPayload {
  promptIdeas: PromptIdea[];
}

export interface SetIsPromptIdeasOpen {
  isPromptIdeasOpen: boolean;
}

export interface ToggleCitationsPayload {
  isCitationsOpen: boolean;
}

export interface SetTotalTokensPayload {
  totalTokens: number;
}

export interface SetDisableAIOperationsPayload {
  numberOfResponseDetails: number;
}

export interface SetElsieSummaryDataPayload {
  kwelloIds?: string[];
  referenceData?: any[];
}

export type DataSource =
  | 'surveys'
  | 'social'
  | 'adboards'
  | 'strategic-documents'
  | 'upload_documents'
  | 'insights'
  | 'other'
  | 'pubmed';

export const DATA_SOURCE_LABEL: Record<DataSource, string> = {
  social: 'Social',
  adboards: 'Ad Board',
  insights: 'Insights',
  upload_documents: 'Uploaded Document',
  other: 'Other',
  'strategic-documents': 'Strategic Documents',
  surveys: 'Surveys',
  pubmed: 'PubMed',
};

interface BaseCitation {
  id: string;
  dataSource: DataSource;
  citationIndex?: number;
  date?: Date;
  responseId: number;
  content?: string;
  profile?: string;
  product?: string;
  productId?: number;
  length?: number;
}

export interface LegacySurveyCitation {
  citationIndex?: number;
  total: string;
  date: Date;
  profile: string;
  specialtyName: string;
  specialtyNameId: number;
  specialty: string;
  fieldTeam: string;
  accountId: number;
  accountCountry: string;
  surveyIsActive: string;
  surveyIsActiveId: number;
  survey: string;
  surveyType: string;
  surveyId: number;
  response: string;
  answer: string;
  responseDetail: string;
  tags: Tags;
  attributes: Attributes;
  territory: string;
  territoryId: number;
  question: string;
  questionId: number;
  questionSortOrder: number;
  responseId: number;
  profileId: number;
  fieldTeamId: number;
  specialtyId: number;
  manager: string;
  managerId: number;
  product: string;
  productId: number;
  questionOptionOrder: number;
  responseOptionId: string;
  responseOption: string;
  isDirectedQuestion: boolean;
  keywords: { [key: string]: Keyword };
  teamId: number;
  team: string;
  accountHierarchyId: number;
  responseOptions: string[];
  weekGroupDay: string;
  monthYear: Date;
  weekYear: Date;
  surveyTargetLink?: string;
}

export type SurveyCitation = BaseCitation &
  LegacySurveyCitation & {
    dataSource: 'surveys' | 'insights';
    teamMember?: string;
    title?: string;
  };
export interface SocialCitation extends BaseCitation {
  dataSource: 'social';
  sourceType?: string; // Twitter / Reddit / …
  reach?: number;
  username?: string;
  avatar?: string;
}
export interface DocumentCitation extends BaseCitation {
  dataSource: 'adboards' | 'strategic-documents' | 'upload_documents' | 'other' | 'pubmed';
  title?: string;
  author?: string;
}
export type Citation = SurveyCitation | SocialCitation | DocumentCitation;

export const isSocialCitation = (c: Citation): c is SocialCitation => c.dataSource === 'social';

export const isDocumentCitation = (c: Citation): c is DocumentCitation =>
  c.dataSource === 'adboards' ||
  c.dataSource === 'strategic-documents' ||
  c.dataSource === 'upload_documents' ||
  c.dataSource === 'other' ||
  c.dataSource === 'pubmed';

export const isSurveyCitation = (c: Citation): c is SurveyCitation =>
  c.dataSource === 'surveys' || c.dataSource === 'insights' || c.dataSource === undefined;

export interface Attributes {
  HCPTier: string;
  PersonTitle: string;
  Persona: string;
}

export interface Keyword {
  name: string;
  significance: number;
}

export interface Tags {
  DiseaseState_241: string;
}

/*
 * Chat History Types
 */

export interface GetChatSessionsRequest {
  projectId: string;
  count?: number;
  continuation_token?: string;
}

export interface GetChatSessionMessagesRequest {
  sessionId: string;
  projectId: string;
  count?: number;
  continuation_token?: string;
}

export interface DeleteChatSessionRequest {
  sessionId: string;
  projectId: string;
}

export interface ChatSessionsResponse {
  sessions: Array<{
    id: string;
    user_id: string;
    project_id: string;
    team_ids: string[];
    title: string;
    timestamp: number;
  }>;
  continuation_token?: string;
}

export interface ChatSessionResponse {
  id: string;
  user_id: string;
  project_id: string;
  team_ids: string[];
  messages: Array<{
    question: string;
    response: {
      chatResponse: string;
      table?: any;
      visualization?: any;
      [key: string]: any;
    };
    timestamp: number;
  }>;
  continuation_token?: string;
}

/*
 * Chat History Redux State Types
 */

export interface ChatHistoryState {
  conversations: ChatSessionsResponse['sessions'];
  currentConversationId: string | null;
  isHistoryLoading: boolean;
  isHistoryOpen: boolean;
  conversationMessages: Record<string, any[]>; // Using any[] to match Kendo Message type
  lastLoadedConversation: string | null;
}

export interface SetChatHistoryConversationsPayload {
  conversations: ChatSessionsResponse['sessions'];
}

export interface SetCurrentConversationPayload {
  conversationId: string | null;
}

export interface SetConversationMessagesPayload {
  conversationId: string;
  messages: any[]; // Using any[] to match Kendo Message type
}

export interface ToggleChatHistoryPayload {
  isHistoryOpen: boolean;
}

export interface SetChatHistoryLoadingPayload {
  isLoading: boolean;
}
